import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Store, CheckCircle2, Clock, AlertTriangle, XCircle, EyeOff, MapPin, Globe, Mail, ExternalLink, Star, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { v: "pending", label: "Pending", filter: "submitted" },
  { v: "approved", label: "Live", filter: "approved" },
  { v: "needs_changes", label: "Changes", filter: "needs_changes" },
  { v: "rejected", label: "Rejected", filter: "rejected" },
  { v: "suspended", label: "Suspended", filter: "suspended" },
  { v: "all", label: "All", filter: "" },
];
const BADGE = {
  approved: { l: "Live", cls: "bg-kid-green/15 text-[#3f9d5c]", ic: CheckCircle2 },
  submitted: { l: "Pending", cls: "bg-kid-sun/20 text-[#9a7b1e]", ic: Clock },
  needs_changes: { l: "Changes", cls: "bg-kid-coral/12 text-kid-coral", ic: AlertTriangle },
  rejected: { l: "Rejected", cls: "bg-kid-coral/12 text-kid-coral", ic: XCircle },
  suspended: { l: "Suspended", cls: "bg-kid-mist text-kid-muted", ic: EyeOff },
  draft: { l: "Draft", cls: "bg-kid-mist text-kid-muted", ic: Clock },
};

function Stat({ label, value, testid }) {
  return <div className="kid-card p-4" data-testid={testid}><div className="text-2xl font-extrabold text-kid-navy font-display">{value}</div><div className="text-sm text-kid-muted">{label}</div></div>;
}

export default function AdminMarketplace() {
  const [kpis, setKpis] = useState({});
  const [mode, setMode] = useState("listings");
  const [tab, setTab] = useState("pending");
  const [providers, setProviders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (t = tab) => {
    setLoading(true);
    const filter = STATUS_TABS.find((x) => x.v === t)?.filter;
    const [{ data: k }, { data: list }] = await Promise.all([
      api.get("/admin/marketplace/dashboard"),
      api.get("/admin/marketplace/listings", { params: filter ? { status: filter } : {} }),
    ]);
    setKpis(k); setProviders(list); setLoading(false);
  }, [tab]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const loadReviews = useCallback(async () => {
    const [{ data: k }, { data: list }] = await Promise.all([
      api.get("/admin/marketplace/dashboard"),
      api.get("/admin/marketplace/reviews", { params: { status: "pending" } }),
    ]);
    setKpis(k); setReviews(list);
  }, []);

  const switchMode = (m) => { setMode(m); if (m === "reviews") loadReviews(); else load(); };

  const moderateReview = async (id, action) => {
    setBusy(true);
    try {
      await api.post(`/admin/marketplace/reviews/${id}/${action}`, {});
      toast.success(action === "approve" ? "Review published." : "Review rejected.");
      loadReviews();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeTab = (v) => { setTab(v); load(v); };
  const openDetail = async (p) => {
    const { data } = await api.get(`/admin/marketplace/listings/${p.id}`);
    setSel(data); setNote(data.admin_notes || "");
  };

  const act = async (action, requireNote = false) => {
    if (requireNote && !note.trim()) { toast.error("Please add a note for the provider."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/marketplace/listings/${sel.id}/${action}`, requireNote ? { note } : {});
      toast.success("Done.");
      setSel(null); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader eyebrow="Marketplace" title="Service Finder" subtitle="Review provider service listings and manage the Ontario provider directory." />
        <Link to="/ops/marketplace/invites" className="kid-btn kid-btn-primary h-11 px-5 whitespace-nowrap shrink-0" data-testid="mk-invite-providers-btn">
          <Mail size={16} /> Invite providers
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Listings" value={kpis.total_listings || 0} testid="mk-stat-total" />
        <Stat label="Pending" value={kpis.pending || 0} testid="mk-stat-pending" />
        <Stat label="Live" value={kpis.approved || 0} testid="mk-stat-live" />
        <Stat label="Orgs" value={kpis.total_orgs || 0} testid="mk-stat-orgs" />
        <Stat label="Reviews to moderate" value={kpis.pending_reviews || 0} testid="mk-stat-reviews" />
        <Stat label="Cities" value={kpis.locations || 0} testid="mk-stat-cities" />
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => switchMode("listings")} className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${mode === "listings" ? "bg-kid-navy text-white" : "bg-white text-kid-muted border border-kid-line hover:bg-kid-mist"}`} data-testid="mk-mode-listings">Listings</button>
        <button onClick={() => switchMode("reviews")} className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors inline-flex items-center gap-1.5 ${mode === "reviews" ? "bg-kid-navy text-white" : "bg-white text-kid-muted border border-kid-line hover:bg-kid-mist"}`} data-testid="mk-mode-reviews">
          <MessageSquare size={14} /> Reviews {kpis.pending_reviews > 0 && <span className="bg-kid-coral text-white text-xs px-1.5 py-0.5 rounded-full">{kpis.pending_reviews}</span>}
        </button>
      </div>

      {mode === "reviews" ? (
        reviews.length === 0 ? (
          <div className="kid-card p-12 text-center text-kid-muted" data-testid="mk-reviews-empty"><MessageSquare size={28} className="mx-auto mb-3 opacity-60" /> No reviews awaiting moderation.</div>
        ) : (
          <div className="space-y-3" data-testid="mk-reviews-list">
            {reviews.map((r) => (
              <div key={r.id} className="kid-card p-5" data-testid="mk-review-row">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} className={n <= r.rating ? "text-kid-sun fill-kid-sun" : "text-kid-line fill-kid-line"} />)}</span>
                      <span className="text-xs text-kid-muted">for <b className="text-kid-navy">{r.business_name}</b> · {(r.service || "").replace(/-/g, " ")}</span>
                    </div>
                    {r.title && <h3 className="font-semibold text-kid-navy mt-1.5">{r.title}</h3>}
                    {r.comment && <p className="text-sm text-kid-navy/90 mt-1 whitespace-pre-wrap">{r.comment}</p>}
                    <div className="text-xs text-kid-muted mt-2">— {r.reviewer_name} · {(r.created_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button onClick={() => moderateReview(r.id, "approve")} disabled={busy} className="bg-kid-green hover:bg-kid-green/90" data-testid="mk-review-approve"><CheckCircle2 size={15} className="mr-1" /> Approve</Button>
                    <Button variant="outline" onClick={() => moderateReview(r.id, "reject")} disabled={busy} className="text-kid-coral border-kid-coral/40" data-testid="mk-review-reject"><XCircle size={15} className="mr-1" /> Reject</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
      <>
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_TABS.map((t) => (
          <button key={t.v} onClick={() => changeTab(t.v)}
            className={`text-sm font-semibold px-3.5 py-1.5 rounded-full transition-colors ${tab === t.v ? "bg-kid-teal text-white" : "bg-white text-kid-muted border border-kid-line hover:bg-kid-mist"}`}
            data-testid={`mk-tab-${t.v}`}>{t.label}</button>
        ))}
      </div>

      {providers.length === 0 ? (
        <div className="kid-card p-12 text-center text-kid-muted" data-testid="mk-empty"><Store size={28} className="mx-auto mb-3 opacity-60" /> No service listings in this view.</div>
      ) : (
        <div className="space-y-3" data-testid="mk-provider-list">
          {providers.map((p) => {
            const b = BADGE[p.profile_status] || BADGE.draft;
            const B = b.ic;
            return (
              <button key={p.id} onClick={() => openDetail(p)} className="kid-card p-5 w-full text-left flex items-center justify-between gap-4 hover:shadow-md transition-shadow" data-testid="mk-provider-row">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${b.cls}`}><B size={12} /> {b.l}</span>
                    <span className="text-xs text-kid-muted capitalize">{(p.primary_category || "").replace(/-/g, " ")}</span>
                    <span className="text-xs text-kid-muted">· {p.provider_type}</span>
                  </div>
                  <h3 className="font-semibold text-kid-navy mt-1">{p.business_name}</h3>
                  <p className="text-sm text-kid-muted">{p.headline || "No headline yet"}</p>
                </div>
                <div className="text-xs text-kid-muted shrink-0 text-right">
                  <div>{(p.cities || []).slice(0, 2).join(", ")}</div>
                  {p.stats && <div className="mt-1">{p.stats.views || 0} views · {p.stats.clicks || 0} clicks · {p.stats.requests || 0} req</div>}
                </div>
              </button>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Detail sheet */}
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="mk-detail-sheet">
          {sel && (
            <>
              <SheetHeader>
                <SheetTitle>{sel.business_name}</SheetTitle>
                <SheetDescription className="capitalize">{sel.provider_type} · {sel.profile_status?.replace(/_/g, " ")}</SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-4 text-sm">
                {sel.headline && <p className="text-kid-navy font-medium">{sel.headline}</p>}
                {sel.short_bio && <p className="text-kid-muted">{sel.short_bio}</p>}
                {sel.detailed_bio && <div><div className="font-semibold text-kid-navy">About</div><p className="text-kid-muted whitespace-pre-wrap">{sel.detailed_bio}</p></div>}
                <div className="flex flex-wrap gap-1.5">{(sel.categories || []).map((c) => <span key={c} className="text-xs bg-kid-mist px-2 py-1 rounded-full capitalize">{c.replace(/-/g, " ")}</span>)}</div>
                <div className="grid grid-cols-2 gap-3 text-kid-muted">
                  <div><b className="text-kid-navy">Contact</b><br />{sel.contact_name}<br />{sel.public_email}</div>
                  <div><b className="text-kid-navy">Cities</b><br />{(sel.cities || []).join(", ") || "—"}</div>
                  <div><b className="text-kid-navy">Ages</b><br />{(sel.age_ranges || []).join(", ") || "—"}</div>
                  <div><b className="text-kid-navy">Accepting</b><br />{sel.accepting_new_clients}</div>
                </div>
                {sel.website_url && <a href={sel.website_url} target="_blank" rel="noopener noreferrer" className="text-kid-teal font-semibold inline-flex items-center gap-1"><Globe size={13} /> {sel.website_url} <ExternalLink size={12} /></a>}
                {(sel.credentials?.regulated === "yes") && (
                  <div className="rounded-xl bg-kid-skytint/50 p-3"><b className="text-kid-navy">Credentials:</b> {sel.credentials.college} {sel.credentials.registration_number && `· #${sel.credentials.registration_number}`}
                    {sel.credentials.public_register_url && <a href={sel.credentials.public_register_url} target="_blank" rel="noopener noreferrer" className="text-kid-teal ml-1">verify</a>}</div>
                )}
                <div className={`rounded-xl p-2 text-xs ${sel.attestation ? "bg-kid-green/10 text-[#3f9d5c]" : "bg-kid-coral/10 text-kid-coral"}`}>{sel.attestation ? "✓ Attestation confirmed" : "⚠ Attestation not confirmed"}</div>

                {sel.stats && (
                  <div className="grid grid-cols-3 gap-2 text-center" data-testid="mk-stats">
                    <div className="kid-card p-3"><div className="text-lg font-bold text-kid-navy">{sel.stats.views || 0}</div><div className="text-xs text-kid-muted">Views</div></div>
                    <div className="kid-card p-3"><div className="text-lg font-bold text-kid-navy">{sel.stats.clicks || 0}</div><div className="text-xs text-kid-muted">Clicks</div></div>
                    <div className="kid-card p-3"><div className="text-lg font-bold text-kid-navy">{sel.stats.requests || 0}</div><div className="text-xs text-kid-muted">Requests</div></div>
                  </div>
                )}

                {/* Note + actions */}
                <div className="pt-2 border-t border-kid-line">
                  <label className="text-sm font-semibold text-kid-navy">Note to provider (for changes/rejection)</label>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1.5 bg-white" placeholder="e.g. Please add your college registration number." data-testid="mk-note" />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {sel.profile_status !== "approved" && <Button onClick={() => act("approve")} disabled={busy} className="bg-kid-green hover:bg-kid-green/90" data-testid="mk-approve"><CheckCircle2 size={15} className="mr-1" /> Approve</Button>}
                    <Button variant="outline" onClick={() => act("request-changes", true)} disabled={busy} data-testid="mk-request-changes"><AlertTriangle size={15} className="mr-1" /> Request changes</Button>
                    <Button variant="outline" onClick={() => act("reject", true)} disabled={busy} className="text-kid-coral border-kid-coral/40" data-testid="mk-reject"><XCircle size={15} className="mr-1" /> Reject</Button>
                    {sel.profile_status === "approved" && <Button variant="outline" onClick={() => act("suspend")} disabled={busy} data-testid="mk-suspend"><EyeOff size={15} className="mr-1" /> Suspend</Button>}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
